import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Nav, Alert, Spinner, Modal } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import styles from '../styles/LoginSignup.module.css';
import logo from '../images/logo.png';
import authApi from '../../api/auth/authApi';
import eventApi from '../../api/events/eventApi';
import locationApi from '../../api/locations/locationApi';
import { STORAGE_KEYS } from '../../api/config';
import AsyncSelect from 'react-select/async';

const LoginSignup = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [signupStep, setSignupStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [existingUserData, setExistingUserData] = useState(null);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [userEventsList, setUserEventsList] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    phoneEmail: '',
    otp: '',
    password: '',
    confirmPassword: '',
    eventType: '',
    eventDate: '',
    eventRegion: '',
    eventTitle: '',
    eventDescription: '',
    acceptTerms: false
  });

  const [eventTypes, setEventTypes] = useState([
    { id: 1, name: 'Harusi', value: 'Harusi' },
    { id: 2, name: 'Kitchen-party', value: 'Kitchen-party' },
    { id: 3, name: 'Send-off', value: 'Send-off' },
    { id: 4, name: 'Engagement', value: 'Engagement' },
  ]);

  React.useEffect(() => {
    fetchEventTypes();
  }, []);

  const fetchEventTypes = async () => {
    try {
      const response = await eventApi.getEventTypes();
      const payload = response?.data?.data || response?.data || response;
      const list = Array.isArray(payload) ? payload : (payload?.event_types || payload?.types || []);
      if (Array.isArray(list) && list.length > 0) {
        const formatted = list.map((item, idx) => {
          if (typeof item === 'string') {
            return { id: idx, name: item, value: item };
          }
          return {
            id: item.id || idx,
            name: item.name || item.title || item.event_type || item.type || String(item),
            value: item.event_type || item.type || item.name || item.title || String(item)
          };
        });
        setEventTypes(formatted);
      }
    } catch (err) {
      console.warn('Could not fetch dynamic event types, using fallback:', err?.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const loadLocationOptions = async (inputValue) => {
    if (inputValue.length < 3) {
      return [];
    }
    const results = await locationApi.searchLocations(inputValue);
    return results.map(loc => ({
      value: loc.id || loc.location_id || loc.name,
      label: loc.name || loc.title || loc.location_name || String(loc),
      id: loc.id || loc.location_id
    }));
  };

  // Helper to open existing events modal or proceed to create new event
  const openEventsModalOrNew = (events) => {
    const list = Array.isArray(events) ? events : [];
    if (list.length > 0) {
      setUserEventsList(list);
      setShowEventsModal(true);
    } else {
      setSignupStep(3);
    }
  };

  const handleSelectEventAndLogin = (evt) => {
    if (evt.event_address) {
      localStorage.setItem(STORAGE_KEYS.UID, evt.event_address);
    }
    if (evt.id) {
      localStorage.setItem(STORAGE_KEYS.EVENT_ID, evt.id);
      localStorage.setItem('selectedEventId', evt.id.toString());
    }
    setShowEventsModal(false);
    toast.success(`Inafungua shughuli: ${evt.event_title || 'Dashibodi'}`);
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 1000);
  };

  const handleCreateNewEventFromModal = () => {
    setShowEventsModal(false);
    setSignupStep(3);
  };

  // Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim()) {
      setError('Tafadhali ingiza jina lako kamili');
      return;
    }

    if (!formData.phoneEmail.trim()) {
      setError('Tafadhali ingiza namba ya simu au barua pepe');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.requestOtp({
        number: formData.phoneEmail
      });

      if (response.success || response.status === 800 || response.data?.otp_sent || (response.status === 500 && response.data?.otp_sent)) {
        setOtpSent(true);
        setSuccessMessage('Namba ya uthibitisho (OTP) imetumwa kikamilifu kwenye namba yako!');
      } else {
        setError(response.message || 'Hitilafu katika kutuma OTP');
      }
    } catch (err) {
      console.error('OTP Request Error:', err);
      setError(err.message || 'Kama umepokea namba ya OTP, ingiza hapa chini');
      setOtpSent(true);
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!formData.otp.trim()) {
      setError('Tafadhali ingiza namba ya uthibitisho');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.verifyOtp({
        number: formData.phoneEmail,
        otp: formData.otp
      });

      if (response.success || response.status === 800) {
        if (response.data) {
          const code = response.data?.authorization_code || response.data?.token || response.data;
          const authCodeStr = code.authorization_code || code.token || code || formData.otp;
          setVerificationToken(authCodeStr);

          if (response.data.token) {
            localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
          }

          const hasUser = Boolean(
            response.data.user_data ||
            response.data.user ||
            response.data.is_existing_user ||
            response.data.is_existing ||
            (response.data.events && response.data.events.length > 0)
          );

          if (hasUser) {
            const userObj = response.data.user_data || response.data.user || {};
            const userDataToStore = { ...userObj };
            if (response.data.events) {
              userDataToStore.events = response.data.events;
            } else if (userObj.events) {
              userDataToStore.events = userObj.events;
            }
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDataToStore));
            if (userDataToStore.id) {
              localStorage.setItem(STORAGE_KEYS.USER_ID, userDataToStore.id);
            }
            setIsExistingUser(true);
            setExistingUserData(response.data);

            const eventsList = response.data?.events || userObj?.events || [];
            if (Array.isArray(eventsList) && eventsList.length > 0) {
              const defaultEvt = eventsList.find(e =>
                e.is_default === 1 || e.is_default === '1' || e.is_default === true ||
                e.pivot?.is_default === 1 || e.pivot?.is_default === '1' || e.pivot?.is_default === true
              ) || eventsList[0];

              localStorage.setItem(STORAGE_KEYS.UID, defaultEvt.event_address);
              localStorage.setItem(STORAGE_KEYS.EVENT_ID, defaultEvt.id);
              localStorage.setItem('selectedEventId', defaultEvt.id?.toString());
              localStorage.setItem('defaultEventId', defaultEvt.id?.toString());
              localStorage.setItem('defaultEvent', JSON.stringify(defaultEvt));
            }

            localStorage.setItem('temp_auth_code', authCodeStr);
            localStorage.setItem('show_change_password_modal', 'true');
            localStorage.setItem('show_select_event_modal', 'true');
            localStorage.setItem('reset_mobile', userObj.mobile || userObj.user_name || formData.phoneEmail);

            setSuccessMessage('Uthibitisho umekamilika! Inakupeleka kwenye dashibodi...');
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 1000);
            return;
          }
        }
        setIsExistingUser(false);
        setSignupStep(2);
        setSuccessMessage('Namba ya OTP imethibitishwa kikamilifu!');
      } else {
        setError(response.message || 'Namba ya uthibitisho si sahihi');
      }
    } catch (err) {
      setError(err.message || 'Hitilafu katika kuthibitisha OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Create Password
  const handleCreatePassword = async (e) => {
    e.preventDefault();

    if (formData.password.length < 6) {
      setError('Password lazima iwe na herufi 6 au zaidi');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Password hazifanani');
      return;
    }

    if (isExistingUser) {
      setLoading(true);
      try {
        const resetResponse = await authApi.resetPassword({
          authorization_code: verificationToken || formData.otp,
          new_password: formData.password,
          mobile: formData.phoneEmail
        });

        if (resetResponse.success || resetResponse.status === 800 || resetResponse.status === 200) {
          setSuccessMessage('Password imetengenezwa kikamilifu!');
          const data = existingUserData;
          if (data?.token) {
            localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
          }
          if (data?.user_data) {
            const userDataToStore = { ...data.user_data };
            if (data.events) {
              userDataToStore.events = data.events;
            }
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDataToStore));
            localStorage.setItem(STORAGE_KEYS.USER_ID, userDataToStore.id);
          }

          let eventsList = data?.events || data?.user_data?.events || [];
          if (!eventsList || eventsList.length === 0) {
            try {
              const userEventsRes = await eventApi.getUserEvents();
              eventsList = userEventsRes?.data?.data || userEventsRes?.data || userEventsRes || [];
            } catch (e) {
              console.log('Error fetching user events:', e);
            }
          }

          openEventsModalOrNew(eventsList);
        } else {
          setError(resetResponse.message || 'Hitilafu katika kutengeneza password mpya');
        }
      } catch (err) {
        setError(err.message || 'Hitilafu. Tafadhali jaribu tena.');
      } finally {
        setLoading(false);
      }
    } else {
      setSignupStep(3);
    }
  };

  // Step 3: Register Event
  const handleSubmitEventDetails = async (e) => {
    e.preventDefault();

    if (!formData.acceptTerms) {
      setError('Tafadhali kubali masharti na kanuni');
      return;
    }

    if (!formData.eventType) {
      setError('Tafadhali chagua aina ya shughuli');
      return;
    }

    if (!formData.eventDate) {
      setError('Tafadhali chagua tarehe ya shughuli');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const isEmail = formData.phoneEmail.includes('@');

      const locationId = formData.location_id || 1;

      const eventData = {
        full_name: formData.fullName,
        user_name: formData.phoneEmail,
        password: formData.password,
        role: 1, // 1 = Event Organizer
        authorization_code: verificationToken || formData.otp,
        location_id: locationId,
        event: {
          event_title: formData.eventTitle || `${formData.fullName}'s ${formData.eventType}`,
          event_address: (formData.eventTitle || `${formData.fullName} ${formData.eventType}`).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Math.floor(Math.random() * 1000),
          event_type: formData.eventType,
          event_date: formData.eventDate,
          event_description: formData.eventDescription || `${formData.eventType} ya ${formData.fullName}`,
          location_id: locationId,
          event_location: formData.eventRegion || 'Tanzania'
        }
      };

      if (isEmail) {
        eventData.email = formData.phoneEmail;
      } else {
        eventData.mobile = parseInt(formData.phoneEmail.replace(/\D/g, ''), 10);
      }

      const response = await authApi.registerEvent(eventData);

      if (response.success || response.status === 800) {
        // Clear any password or event selector modal flags for new registration
        localStorage.removeItem('show_change_password_modal');
        localStorage.removeItem('show_select_event_modal');
        localStorage.removeItem('temp_auth_code');
        localStorage.removeItem('reset_mobile');

        // Store authentication data
        if (response.data?.token) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
        }

        if (response.data?.user_data) {
          const userDataToStore = { ...response.data.user_data };
          if (response.data.events) {
            userDataToStore.events = response.data.events;
          }
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDataToStore));
          localStorage.setItem(STORAGE_KEYS.USER_ID, userDataToStore.id);
        }

        const eventsList = response.data?.events || response.data?.user_data?.events || [];
        if (Array.isArray(eventsList) && eventsList.length > 0) {
          const defaultEvt = eventsList.find(e =>
            e.is_default === 1 || e.is_default === '1' || e.is_default === true ||
            e.pivot?.is_default === 1 || e.pivot?.is_default === '1' || e.pivot?.is_default === true
          ) || eventsList[0];

          localStorage.setItem(STORAGE_KEYS.UID, defaultEvt.event_address);
          localStorage.setItem(STORAGE_KEYS.EVENT_ID, defaultEvt.id);
          localStorage.setItem('selectedEventId', defaultEvt.id?.toString());
          localStorage.setItem('defaultEventId', defaultEvt.id?.toString());
          localStorage.setItem('defaultEvent', JSON.stringify(defaultEvt));
        }

        // Flag to trigger onboarding tour on dashboard
        localStorage.setItem('isNewUserOnboarding', 'true');

        setSuccessMessage('Akaunti imefanikiwa kufunguliwa! Inakupeleka kwenye dashibodi...');

        // Redirect based on user role
        setTimeout(() => {
          const userRole = response.data?.user_data?.role;
          if (userRole === 1) {
            window.location.href = '/dashboard';
          } else if (userRole === 3 || userRole === 'provider' || response.data?.user_data?.event_type === 'Watoa Huduma') {
            window.location.href = '/service-provider';
          } else {
            window.location.href = '/dashboard';
          }
        }, 1500);
      } else {
        setError(response.message || 'Hitilafu katika kusajili shughuli');
      }
    } catch (err) {
      setError(err.message || 'Hitilafu katika kusajili. Tafadhali jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  // Login handler
  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    const username = e.target.username.value;
    const password = e.target.password.value;

    if (!username || !password) {
      setError('Tafadhali jaza namba/barua pepe na password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let loginUsername = username.trim();
      const response = await authApi.login({ username: loginUsername, password });

      if (response.success || response.status === 800) {
        localStorage.removeItem('show_change_password_modal');
        localStorage.removeItem('show_select_event_modal');
        localStorage.removeItem('temp_auth_code');
        localStorage.removeItem('reset_mobile');

        if (response.data?.token) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
        }

        if (response.data?.user_data) {
          const userDataToStore = { ...response.data.user_data };
          if (response.data.events) {
            userDataToStore.events = response.data.events;
          }
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDataToStore));
          localStorage.setItem(STORAGE_KEYS.USER_ID, userDataToStore.id);
        }

        const eventsList = response.data?.events || response.data?.user_data?.events || [];
        if (Array.isArray(eventsList) && eventsList.length > 0) {
          const defaultEvt = eventsList.find(e =>
            e.is_default === 1 || e.is_default === '1' || e.is_default === true ||
            e.pivot?.is_default === 1 || e.pivot?.is_default === '1' || e.pivot?.is_default === true
          ) || eventsList[0];

          localStorage.setItem(STORAGE_KEYS.UID, defaultEvt.event_address);
          localStorage.setItem(STORAGE_KEYS.EVENT_ID, defaultEvt.id);
          localStorage.setItem('selectedEventId', defaultEvt.id?.toString());
          localStorage.setItem('defaultEventId', defaultEvt.id?.toString());
          localStorage.setItem('defaultEvent', JSON.stringify(defaultEvt));
        }

        setSuccessMessage('Karibu tena! Inakupeleka kwenye dashibodi...');

        setTimeout(() => {
          const userRole = response.data?.user_data?.role;
          if (userRole === 1) {
            window.location.href = '/dashboard';
          } else if (userRole === 3 || userRole === 'provider' || response.data?.user_data?.event_type === 'Watoa Huduma') {
            window.location.href = '/service-provider';
          } else {
            window.location.href = '/dashboard';
          }
        }, 1000);
      } else {
        setError(response.message || 'Namba/barua pepe au namba ya siri si sahihi');
      }
    } catch (err) {
      setError(err.message || 'Hitilafu katika kuingia. Tafadhali jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  const renderSignupStep = () => {
    switch (signupStep) {
      case 1:
        return (
          <>
            <div className="text-center mb-4">
              <p>Anza kwa kutengeneza account yako</p>
            </div>

            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

            <Form onSubmit={otpSent ? handleVerifyOTP : handleRequestOTP}>
              <Form.Group className="mb-3" controlId="formBasicName">
                <Form.Control
                  type="text"
                  placeholder="Jina kamili"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="formBasicPhoneEmail">
                <Form.Control
                  type="text"
                  placeholder="Namba ya Simu / Email"
                  name="phoneEmail"
                  value={formData.phoneEmail}
                  onChange={handleInputChange}
                  disabled={loading || otpSent}
                  required
                />
              </Form.Group>

              {otpSent ? (
                <>
                  <Form.Group className="mb-3" controlId="formBasicOTP">
                    <Form.Label>Thibitisha Nambari (OTP)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingiza nambari uliyotumwa"
                      name="otp"
                      value={formData.otp}
                      onChange={handleInputChange}
                      disabled={loading}
                      required
                    />
                    <Form.Text className="text-muted">
                      Nambari ya uthibitisho imetumwa kwenye {formData.phoneEmail}
                    </Form.Text>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100"
                    disabled={loading}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Thibitisha OTP'}
                  </Button>

                  <div className="text-center mt-3">
                    <Button
                      variant="link"
                      onClick={() => {
                        setOtpSent(false);
                        setFormData({ ...formData, otp: '' });
                      }}
                      disabled={loading}
                    >
                      Tuma tena OTP
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100"
                  disabled={loading}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : 'Omba OTP'}
                </Button>
              )}
            </Form>
          </>
        );

      case 2:
        return (
          <>
            <div className="text-center mb-4">
              <p>{isExistingUser ? 'Tengeneza password mpya kwa ajili ya akaunti yako' : 'Weka password itakayotumika kuingia kwenye account yako'}</p>
            </div>

            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
            {successMessage && <Alert variant="success" className="mb-3">{successMessage}</Alert>}

            <Form onSubmit={handleCreatePassword}>
              <Form.Group className="mb-3" controlId="formBasicPassword">
                <Form.Label>Password</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Weka password (herufi 6 au zaidi)"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    style={{ paddingRight: '42px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    style={{
                      position: 'absolute', right: '12px', top: '50%',
                      transform: 'translateY(-50%)', background: 'none',
                      border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0
                    }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ficha password' : 'Onyesha password'}
                  >
                    {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formBasicConfirmPassword">
                <Form.Label>Thibitisha Password</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Rudia password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    style={{ paddingRight: '42px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(p => !p)}
                    style={{
                      position: 'absolute', right: '12px', top: '50%',
                      transform: 'translateY(-50%)', background: 'none',
                      border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0
                    }}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Ficha password' : 'Onyesha password'}
                  >
                    {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
              </Form.Group>

              <Button variant="primary" type="submit" className="w-100">
                Endelea
              </Button>
            </Form>
          </>
        );

      case 3:
        return (
          <>
            <div className="text-center mb-4">
              <h4>Tengeneza Event</h4>
              <p>Jaza taarifa zinazo hitajika kuhusu shughuli yako</p>
            </div>

            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
            {successMessage && <Alert variant="success" className="mb-3">{successMessage}</Alert>}

            <Form onSubmit={handleSubmitEventDetails}>
              <Form.Group className="mb-3" controlId="formBasicEventTitle">
                <Form.Label>Jina la Shughuli (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Mfano: Harusi ya Juma na Asha"
                  name="eventTitle"
                  value={formData.eventTitle}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  Ukiacha, tutatumia jina lako na aina ya shughuli
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formBasicEventType">
                <Form.Label>Aina ya Shughuli *</Form.Label>
                <Form.Select
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Chagua...</option>
                  {Array.isArray(eventTypes) && eventTypes.map((type, idx) => (
                    <option key={type.id || idx} value={type.value || type.name}>
                      {type.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formBasicEventDate">
                <Form.Label>Tarehe ya shughuli *</Form.Label>
                <Form.Control
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.preventDefault()}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="formBasicEventRegion">
                <Form.Label>Mkoa / Mahali</Form.Label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions={false}
                  loadOptions={loadLocationOptions}
                  placeholder="Mfano: Arusha , Dar es salaam"
                  noOptionsMessage={({ inputValue }) =>
                    inputValue.length < 3 ? "Andika walau herufi 3 kutafuta" : "Hakuna mahali palipopatikana"
                  }
                  onChange={(selectedOption) => {
                    setFormData({
                      ...formData,
                      eventRegion: selectedOption ? selectedOption.label : '',
                      location_id: selectedOption ? (selectedOption.id || selectedOption.value) : null
                    });
                  }}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#4169e1' : '#ced4da',
                      borderRadius: '0.375rem',
                      padding: '2px'
                    })
                  }}
                />
              </Form.Group>


              <Form.Group className="mb-3" controlId="formBasicTerms">
                <Form.Check
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  label={
                    <span>
                      Nakubali{' '}
                      <Link to="/terms" target="_blank" className="text-primary">
                        Terms and Conditions
                      </Link>{' '}
                      za Harusi Online
                    </span>
                  }
                  required
                />
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                className="w-100"
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Maliza na Anza'}
              </Button>
            </Form>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Container fluid className={`${styles.loginSignupContainer} py-5`}>
      <br /><br /><br />
      <Row className="justify-content-center align-items-center">
        <Col xs={12} lg={8} xl={9}>
          <Card className="shadow-lg">
            <Card.Body className="p-0">
              <Row className="g-0">
                <Col md={6} lg={5} className={`${styles.leftSection} p-5`}>
                  <div className="text-center">
                    <img src={logo} alt="Harusi Online" className={`${styles.logo} mb-4 d-none d-md-inline-block`} />
                    <h2 className="fw-bold mb-2 text-white">Karibu,</h2>
                    <p className="text-white mb-0">Karibu Harusionline, Ingia kuendelea</p>
                  </div>
                </Col>

                <Col md={6} lg={7} className="p-4">
                  <Nav variant="pills" className="nav-fill mb-4">
                    <Nav.Item>
                      <Nav.Link
                        eventKey="login"
                        active={activeTab === 'login'}
                        onClick={() => {
                          setActiveTab('login');
                          setSignupStep(1);
                          setOtpSent(false);
                          setError('');
                        }}
                        className={activeTab === 'login' ? styles.activeNav : ''}
                      >
                        Ingia
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="signup"
                        active={activeTab === 'signup'}
                        onClick={() => {
                          setActiveTab('signup');
                          setSignupStep(1);
                          setOtpSent(false);
                          setError('');
                        }}
                        className={activeTab === 'signup' ? styles.activeNav : ''}
                      >
                        Jisajili
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  {activeTab === 'login' ? (
                    <Form onSubmit={handleLoginSubmit}>
                      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                      {successMessage && <Alert variant="success" className="mb-3">{successMessage}</Alert>}

                      <Form.Group className="mb-3" controlId="formBasicUsername">
                        <Form.Control
                          type="text"
                          placeholder="Namba ya Simu / Email"
                          name="username"
                          disabled={loading}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="formBasicPassword">
                        <div style={{ position: 'relative' }}>
                          <Form.Control
                            type={showLoginPassword ? 'text' : 'password'}
                            placeholder="Password"
                            name="password"
                            disabled={loading}
                            required
                            style={{ paddingRight: '42px' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowLoginPassword(p => !p)}
                            style={{
                              position: 'absolute', right: '12px', top: '50%',
                              transform: 'translateY(-50%)', background: 'none',
                              border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0
                            }}
                            tabIndex={-1}
                            aria-label={showLoginPassword ? 'Ficha password' : 'Onyesha password'}
                          >
                            {showLoginPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                          </button>
                        </div>
                      </Form.Group>

                      <div className="text-end mb-3">
                        <Link to="/forgot-password" className="text-primary text-decoration-none">
                          Nimesahau Password
                        </Link>
                      </div>

                      <Button
                        variant="primary"
                        type="submit"
                        className="w-100"
                        disabled={loading}
                      >
                        {loading ? <Spinner animation="border" size="sm" /> : 'Ingia'}
                      </Button>
                    </Form>
                  ) : (
                    renderSignupStep()
                  )}

                  {activeTab === 'login' && (
                    <div className="mt-4 text-center">
                      <p>
                        Hauna akaunti ya Harusionline?{' '}
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => {
                            setActiveTab('signup');
                            setSignupStep(1);
                            setOtpSent(false);
                            setError('');
                          }}
                          style={{ color: '#0d6efd' }}
                        >
                          Jisajili BURE sasa
                        </Button>
                      </p>
                    </div>
                  )}

                  {activeTab === 'signup' && signupStep === 1 && !otpSent && (
                    <div className="mt-4 text-center">
                      <p>
                        Una akaunti tayari?{' '}
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => {
                            setActiveTab('login');
                            setSignupStep(1);
                            setOtpSent(false);
                          }}
                          style={{ color: '#0d6efd' }}
                        >
                          Ingia
                        </Button>
                      </p>
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginSignup;